import type { Locale } from '@/i18n/locales'

/**
 * The currency every amount reaches this layer in. Facts, WAPI and psearch all quote
 * canonical amounts; a display currency exists only at render
 * (→ invariants.md#facts-canonical-store-editorial).
 */
export const CANONICAL_CURRENCY = 'USD'

/** The currencies this app is willing to state a price in. */
export const DISPLAY_CURRENCIES = ['USD', 'MXN'] as const

export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number]

/**
 * Each served locale's default display currency — the served-locale rows of the shared
 * Wanderu locale→currency mapping, adopted rather than invented. Repointing a
 * locale, or adding one, is a change to a convention other surfaces also apply: a visitor
 * moving between surfaces keeps their currency only because both read the same table.
 *
 * Typed on `Locale` so a new served locale is a compile error until someone decides which
 * currency it prices in, rather than silently falling back.
 */
export const LOCALE_DEFAULT_CURRENCY: Record<Locale, DisplayCurrency> = {
    'en-us': 'USD',
    'es-mx': 'MXN',
}

/**
 * The currencies the **legacy application** is willing to state a price in — its own
 * `supportedCurrencies` list, mirrored here rather than derived, since nothing in this
 * repo can read it.
 *
 * A superset of `DISPLAY_CURRENCIES`: legacy serves every locale Wanderu has, so it prices
 * in currencies this app has no locale for. A URL naming **any** of these — the two this
 * app also displays included — is one legacy has a page for, and is handed to it rather
 * than stripped.
 *
 * Mirroring costs a manual update when legacy's list changes. The cost of drifting is
 * small and one-directional: a currency legacy has dropped would be proxied to a surface
 * that no longer honours it (which is what legacy already does with any such value), and
 * one legacy has added would be redirected away here.
 */
export const LEGACY_DISPLAY_CURRENCIES = [
    'CAD',
    'CHF',
    'CZK',
    'DKK',
    'EUR',
    'GBP',
    'HRK',
    'HUF',
    'MXN',
    'NOK',
    'PLN',
    'RON',
    'SEK',
    'TRY',
    'USD',
] as const

export function isDisplayCurrency(value: string): value is DisplayCurrency {
    return (DISPLAY_CURRENCIES as readonly string[]).includes(value)
}

/** Whether the legacy application displays this currency. */
export function isLegacyDisplayCurrency(value: string): boolean {
    return (LEGACY_DISPLAY_CURRENCIES as readonly string[]).includes(value)
}
